import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTelegram } from '@/hooks/useTelegram';

interface MagicItem {
  name: string;
  type: string;
  description: string;
  tags?: string[];
}

const magicItems: MagicItem[] = [
  {
    name: 'Меч Света',
    type: 'Оружие',
    description: 'Светится в присутствии нежити. +1 урон против нежити и демонов.',
    tags: ['+1 урон', 'Сияние'],
  },
  {
    name: 'Кольцо Невидимости',
    type: 'Кольцо',
    description: 'Надев кольцо, вы становитесь невидимым. Но кольцо шепчет и искушает...',
    tags: ['Невидимость', 'Проклятие'],
  },
  {
    name: 'Зелье Лечения',
    type: 'Зелье',
    description: 'Восстанавливает 10 HP при употреблении.',
    tags: ['Одноразовое', '10 HP'],
  },
  {
    name: 'Плащ Эльфов',
    type: 'Броня',
    description: 'Помогает слиться с окружением в лесу. +1 к Хитрости в лесу.',
    tags: ['+1 Хитрость', 'Лес'],
  },
  {
    name: 'Посох Мага',
    type: 'Оружие',
    description: 'Накапливает магическую энергию. Можно потратить заряд чтобы усилить заклинание.',
    tags: ['3 заряда', 'Близко'],
  },
  {
    name: 'Сумка Хранения',
    type: 'Сумка',
    description: 'Внутри больше, чем снаружи. Вмещает 10 веса, но весит только 1.',
    tags: ['10 веса', 'Магия'],
  },
  {
    name: 'Ботинки Скорости',
    type: 'Броня',
    description: 'Двигайтесь быстрее ветра. Можете действовать первым в бою.',
    tags: ['Скорость', 'Инициатива'],
  },
  {
    name: 'Амулет Защиты',
    type: 'Амулет',
    description: 'Один раз поглощает смертельный удар, затем рассыпается в прах.',
    tags: ['Одноразовое', 'Защита'],
  },
];

const treasureTables = [
  {
    title: 'Монеты и драгоценности',
    items: [
      '1-2: Горсть медяков (1d6 монет)',
      '3-4: Серебро и медь (2d6 монет)',
      '5-6: Золотые монеты (1d6 × 10 монет)',
      '7-8: Драгоценный камень (стоит 2d6 × 10 монет)',
      '9-10: Ювелирное украшение (стоит 3d6 × 10 монет)',
      '11-12: Сундук с сокровищами (4d6 × 10 монет)',
    ],
  },
  {
    title: 'Магические предметы',
    items: [
      '1-2: Зелье (лечение, сила, скорость)',
      '3-4: Свиток с заклинанием',
      '5-6: Волшебное оружие (+1)',
      '7-8: Волшебная броня (+1)',
      '9-10: Жезл или посох',
      '11-12: Редкий артефакт',
    ],
  },
];

export function TreasuresPage() {
  const navigate = useNavigate();
  const { hapticFeedback } = useTelegram();

  return (
    <div className="min-h-screen bg-gray-900 safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="sticky top-0 bg-gray-900/95 backdrop-blur-sm border-b border-gray-800 z-10">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => {
              hapticFeedback('light');
              navigate('/gm-tools');
            }}
            className="text-gray-400 hover:text-white transition-colors"
          >
            ← Назад
          </button>
          <h1 className="text-lg font-bold text-white">Сокровища</h1>
          <div className="w-16" />
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-6">
        {/* Treasure Tables */}
        <section>
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-lg font-bold text-amber-400 mb-4"
          >
            🎲 Таблицы сокровищ
          </motion.h2>

          {treasureTables.map((table, tableIndex) => (
            <motion.div
              key={tableIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * tableIndex }}
              className="mb-4 p-4 rounded-xl bg-gray-800/50 border border-gray-700/50"
            >
              <h3 className="font-medium text-white mb-3">{table.title}</h3>
              <div className="space-y-1">
                {table.items.map((item, i) => (
                  <p key={i} className="text-gray-300 text-sm">{item}</p>
                ))}
              </div>
            </motion.div>
          ))}
        </section>

        {/* Magic Items */}
        <section>
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-lg font-bold text-amber-400 mb-4"
          >
            ✨ Магические предметы
          </motion.h2>

          <div className="space-y-3">
            {magicItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + 0.05 * index }}
                className="p-4 rounded-xl bg-gray-800/50 border border-gray-700/50"
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-white">{item.name}</h3>
                  <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-400">
                    {item.type}
                  </span>
                </div>
                <p className="text-gray-300 text-sm mb-2">{item.description}</p>
                {item.tags && (
                  <div className="flex flex-wrap gap-1">
                    {item.tags.map((tag, i) => (
                      <span key={i} className="px-2 py-0.5 text-xs rounded bg-gray-700 text-gray-300">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
